import { AfterViewInit, AfterContentChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { CommonService } from 'src/app/_services/common.service';
import { BrandingService } from 'src/app/_services/branding.service';
import { ThemeService } from 'src/app/_services/theme.service';
import { AuthService } from 'src/app/_services/auth.service';
import { filter } from 'rxjs/operators';
import { HeaderConfig, BrandingConfig, DEFAULT_HEADER_CONFIG, MenuItem } from 'src/app/_models/header-config.model';

declare let gtag: Function;

@Component({
  selector: 'tangram-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss']
})
export class LayoutComponent implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy {
  isEditProfileSubScription: Subscription;
  addClassInDiv: boolean = true;
  isFooterVisible: boolean = true;
  isEmbedded: boolean = false;

  // Header configuration
  headerConfig: HeaderConfig = { ...DEFAULT_HEADER_CONFIG };
  branding: BrandingConfig = { logoUrl: null, bannerUrl: null };

  // Subscriptions
  private routeSub: Subscription;
  private brandingSub: Subscription;
  private userSub: Subscription;

  // User state
  isUserLoggedIn = false;
  currentUser: any = null;

  constructor(
    private commonService: CommonService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private brandingService: BrandingService,
    private themeService: ThemeService,
    private authService: AuthService,
    private renderer: Renderer2
  ) {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user) {
      gtag('event', 'page_visit', {
        'event_category': user._id,
        'event_label': this.router.url
      });
    }
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  ngOnInit() {
    this.isEditProfileSubScription = this.commonService.onChangeView.subscribe((param: any) => {
      this.addClassInDiv = param == 'edit-profile' ? false : true;
    });

    if (this.router.url === '/my-tangram' || this.router.url === '/configuration') {
      this.isFooterVisible = true;
    } else if (this.router.url.includes('embedded')) {
      this.isFooterVisible = false;
      this.isEmbedded = true;
    }

    // Subscribe to user auth state
    this.userSub = this.authService.currentUser.subscribe(user => {
      this.isUserLoggedIn = !!user;
      this.currentUser = user;
      // Rebuild header config when auth state changes
      this.headerConfig = this.buildHeaderConfig(this.router.url);
    });

    // Subscribe to branding changes
    this.brandingSub = this.brandingService.activeBranding$.subscribe(branding => {
      this.branding = {
        logoUrl: branding && branding.logoUrl ? branding.logoUrl : null,
        bannerUrl: branding && branding.bannerUrl ? branding.bannerUrl : null
      };
    });
    this.brandingService.refreshActiveBranding().subscribe();

    // Subscribe to route changes and update header config
    this.routeSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.headerConfig = this.buildHeaderConfig(event.urlAfterRedirects);
        this.updateFooterVisibility(event.urlAfterRedirects);
        this.updateHomePageClass(event.urlAfterRedirects);
      });

    // Initial header config
    this.headerConfig = this.buildHeaderConfig(this.router.url);
    this.updateHomePageClass(this.router.url);
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.isEditProfileSubScription ? this.isEditProfileSubScription.unsubscribe() : '';
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
    if (this.brandingSub) {
      this.brandingSub.unsubscribe();
    }
    if (this.userSub) {
      this.userSub.unsubscribe();
    }
    // Clean up body class
    this.renderer.removeClass(document.body, 'home-page');
  }

  private updateFooterVisibility(url: string) {
    if (url === '/my-tangram' || url === '/configuration') {
      this.isFooterVisible = true;
    } else if (url.includes('embedded')) {
      this.isFooterVisible = false;
      this.isEmbedded = true;
    } else {
      this.isFooterVisible = true;
      this.isEmbedded = false;
    }
  }

  private updateHomePageClass(url: string) {
    // HashLocationStrategy is used, so URLs have # prefix (e.g., #/home)
    const normalizedUrl = url.startsWith('#') ? url.substring(1) : url;
    const isHomePage = normalizedUrl === '/home' || normalizedUrl === '/home/';

    if (isHomePage) {
      this.renderer.addClass(document.body, 'home-page');
    } else {
      this.renderer.removeClass(document.body, 'home-page');
    }
  }

  private getMiddleSectionText(url: string): { text: string, show: boolean } {
    // HashLocationStrategy is used, so URLs have # prefix (e.g., #/home)
    const normalizedUrl = url.startsWith('#') ? url.substring(1) : url;
    const isHomePage = normalizedUrl === '/home' || normalizedUrl === '/home/';
    const isContactUsPage = normalizedUrl === '/home/contact-us';
    const isPolicyPage = normalizedUrl == '/home/privacy-policy';
    switch (true) {
      case isHomePage:
        return { text: 'Organize online content from across <br/>the internet into one experience.', show: true };
      case isContactUsPage:
        return { text: 'Contact Us', show: true };
      case isPolicyPage:
        return { text: 'Privacy Policy', show: true };
      default:
        return { text: null, show: false };
    }
  }

  private buildHeaderConfig(url: string): HeaderConfig {
    // Build menu items based on auth state and route
    const menuItems: MenuItem[] = [];
    // HashLocationStrategy is used, so URLs have # prefix (e.g., #/home)
    const normalizedUrl = url.startsWith('#') ? url.substring(1) : url;
    const isHomePage = normalizedUrl === '/home' || normalizedUrl === '/home/';
    return {
      showLogo: true,
      showMiddleSection: this.getMiddleSectionText(url).show,
      middleSectionText: this.getMiddleSectionText(url).text,
      showThemeToggle: true,
      showUnlockPremium: this.currentUser && this.currentUser.subscriptionActive ? false : true,
      menuItems: menuItems,
      isHomePage
    };
  }

  toggleSidebar() {
    // Handled by header component internally
  }

  toggleTheme() {
    // Handled by header component internally
  }
}
